import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, List, CheckCircle, XCircle, AlertTriangle, Loader, Bell, BellRing, Eye, RotateCcw, UserCheck, UserX, MessageCircle } from 'lucide-react';
import Card3D from './Card3D';
import { institutionAPI } from '../api/api';

const InstitutionDashboard: React.FC = () => {
  const [records, setRecords] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [verificationRequests, setVerificationRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showVerificationRequests, setShowVerificationRequests] = useState(false);
  const [searchParams, setSearchParams] = useState({
    roll_number: '',
    name: '',
    status: ''
  });
  
  const [formData, setFormData] = useState({
    roll_number: '',
    full_name: '',
    current_year_sem: '',
    passed_out: '',
    gmail: '',
    phone_number: '',
    cgpa: '',
    backlogs: '',
    remarks: ''
  });

  // Load records
  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      const data: any = await institutionAPI.list(searchParams);
      setRecords(data.records || []);
      setError('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      const data: any = await institutionAPI.getNotifications();
      setNotifications(data.notifications || []);
    } catch (err: any) {
      console.error('Failed to load notifications:', err.message);
    }
  }, []);

  // Load verification requests
  const loadVerificationRequests = useCallback(async () => {
    try {
      const data: any = await institutionAPI.getVerificationRequests();
      setVerificationRequests(data.pending_requests || []);
    } catch (err: any) {
      console.error('Failed to load verification requests:', err.message);
    }
  }, []);

  useEffect(() => {
    loadRecords();
    loadNotifications();
    loadVerificationRequests();
    
    // Refresh notifications every 30 seconds
    const interval = setInterval(() => {
      loadNotifications();
      loadVerificationRequests();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loadRecords, loadNotifications, loadVerificationRequests]);

  const handleCreateRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await institutionAPI.create(formData);
      
      setSuccess('✅ Academic record created successfully!');
      setFormData({
        roll_number: '',
        full_name: '',
        current_year_sem: '',
        passed_out: '',
        gmail: '',
        phone_number: '',
        cgpa: '',
        backlogs: '',
        remarks: ''
      });
      
      await loadRecords();
      await loadNotifications();
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRecord = async (rollNumber: string, updateData: any) => {
    try {
      await institutionAPI.update(rollNumber, updateData);
      await loadRecords();
      await loadNotifications();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const handleDeleteRecord = async (rollNumber: string, reason: string) => {
    try {
      await institutionAPI.delete(rollNumber, { reason, deleted_by: 'institution_admin' });
      await loadRecords();
      await loadNotifications();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const handleRestoreRecord = async (rollNumber: string) => {
    const reason = prompt('💡 Why are you restoring this record? (e.g., Re-admission, Data correction):');
    if (reason !== null && reason.trim()) {
      try {
        await institutionAPI.restore(rollNumber, { reason, restored_by: 'institution_admin' });
        await loadRecords();
        await loadNotifications();
        setSuccess(`✅ Record restored: ${rollNumber.toUpperCase()}`);
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleApproveVerification = async (requestData: any) => {
    try {
      await institutionAPI.approveVerification({
        roll_number: requestData.roll_number,
        request_id: requestData.request.id,
        approved_fields: requestData.request.requested_fields,
        approved_by: 'institution_admin'
      });
      
      await loadVerificationRequests();
      await loadNotifications();
      setSuccess(`✅ Verification approved for ${requestData.student_name}`);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSearch = async () => {
    await loadRecords();
  };

  const markNotificationsAsRead = async () => {
    try {
      await institutionAPI.getNotifications(true); // Mark as read
      await loadNotifications();
    } catch (err: any) {
      console.error('Failed to mark notifications as read:', err.message);
    }
  };

  const getNotificationIcon = (type: string) => {
    const icons = {
      student_deleted: <UserX className="h-4 w-4 text-red-400" />,
      student_restored: <UserCheck className="h-4 w-4 text-neon-green" />,
      verification_request: <MessageCircle className="h-4 w-4 text-neon-blue" />,
      verification_approved: <CheckCircle className="h-4 w-4 text-neon-purple" />,
      mistake_reported: <AlertTriangle className="h-4 w-4 text-yellow-400" />
    };
    return icons[type as keyof typeof icons] || <Bell className="h-4 w-4 text-gray-400" />;
  };

  const getNotificationColor = (priority: string) => {
    const colors = {
      high: 'border-red-400/50 bg-red-400/10',
      normal: 'border-neon-blue/50 bg-neon-blue/10',
      low: 'border-gray-400/50 bg-gray-400/10'
    };
    return colors[priority as keyof typeof colors] || colors.normal;
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;

  return (
    <motion.div 
      className="min-h-screen p-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header with Notifications */}
        <motion.div 
          className="flex justify-between items-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div>
            <h1 className="text-7xl font-bold bg-gradient-to-r from-neon-blue to-neon-purple bg-clip-text text-transparent mb-2">
              🏛️ Institution Dashboard
            </h1>
            <p className="text-2xl text-gray-300">
              Manage academic records, approve verifications, and maintain institutional integrity
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Verification Requests */}
            <motion.button
              onClick={() => setShowVerificationRequests(!showVerificationRequests)}
              className="relative p-3 glass-card rounded-lg hover:bg-neon-purple/10 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MessageCircle className="h-6 w-6 text-neon-purple" />
              {verificationRequests.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-neon-purple text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {verificationRequests.length}
                </span>
              )}
            </motion.button>
            
            {/* Notifications */}
            <motion.button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-3 glass-card rounded-lg hover:bg-yellow-400/10 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {unreadNotifications > 0 ? (
                <BellRing className="h-6 w-6 text-yellow-400" />
              ) : (
                <Bell className="h-6 w-6 text-gray-400" />
              )}
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadNotifications}
                </span>
              )}
            </motion.button>

            {/* Refresh Button */}
            <motion.button
              onClick={async () => {
                await loadRecords();
                await loadNotifications();
                await loadVerificationRequests();
              }}
              className="p-3 glass-card rounded-lg hover:bg-neon-blue/10 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RotateCcw className="h-6 w-6 text-neon-blue" />
            </motion.button>
          </div>
        </motion.div>

        {/* Success/Error Messages */}
        {error && (
          <motion.div 
            className="alert-error p-4 rounded-lg flex items-center space-x-3 mb-6"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <XCircle className="h-5 w-5 text-red-400" />
            <span>{error}</span>
          </motion.div>
        )}

        {success && (
          <motion.div 
            className="alert-success p-4 rounded-lg flex items-center space-x-3 mb-6"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span>{success}</span>
          </motion.div>
        )}

        {/* Verification Requests Panel */}
        <AnimatePresence>
          {showVerificationRequests && (
            <motion.div 
              className="glass-card p-6 rounded-xl mb-8"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <MessageCircle className="h-5 w-5 mr-2 text-neon-purple" />
                  Pending Verification Requests ({verificationRequests.length})
                </h3>
                <button
                  onClick={() => setShowVerificationRequests(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              {verificationRequests.length === 0 ? (
                <p className="text-gray-400 text-center py-4">No pending verification requests</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {verificationRequests.map((request, index) => (
                    <motion.div 
                      key={request.request.id}
                      className="p-4 glass-card rounded-lg border border-neon-purple/30"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <MessageCircle className="h-4 w-4 text-neon-purple" />
                            <span className="text-white font-medium">
                              {request.student_name} ({request.roll_number})
                            </span>
                          </div>
                          <p className="text-sm text-gray-300 mb-2">
                            Verifier: <span className="text-neon-blue">{request.request.verifier_id}</span>
                          </p>
                          <p className="text-xs text-gray-400 mb-3">
                            Fields: {request.request.requested_fields.join(', ')}
                          </p>
                          <p className="text-xs text-gray-400">
                            Requested: {new Date(request.request.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <motion.button
                          onClick={() => handleApproveVerification(request)}
                          className="px-3 py-1 bg-neon-green hover:bg-green-600 text-white rounded text-sm font-medium transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Approve
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Notifications Panel */}
        <AnimatePresence>
          {showNotifications && (
            <motion.div 
              className="glass-card p-6 rounded-xl mb-8"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <Bell className="h-5 w-5 mr-2 text-yellow-400" />
                  Institution Notifications ({notifications.length})
                </h3>
                <div className="flex items-center space-x-2">
                  {unreadNotifications > 0 && (
                    <button
                      onClick={markNotificationsAsRead}
                      className="text-sm text-neon-blue hover:text-blue-300"
                    >
                      Mark all as read
                    </button>
                  )}
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              {notifications.length === 0 ? (
                <p className="text-gray-400 text-center py-4">No notifications yet</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {notifications.slice(0, 10).map((notification, index) => (
                    <motion.div 
                      key={notification.id}
                      className={`p-3 rounded-lg border ${getNotificationColor(notification.priority)} ${!notification.read ? 'font-medium' : 'opacity-70'}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="flex items-start space-x-3">
                        {getNotificationIcon(notification.type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm break-words">{notification.message}</p>
                          <div className="flex justify-between items-center mt-1">
                            <p className="text-xs text-gray-400">
                              {new Date(notification.timestamp).toLocaleString()}
                            </p>
                            {notification.data?.roll_number && notification.type === 'student_deleted' && (
                              <button
                                onClick={() => handleRestoreRecord(notification.data.roll_number.toLowerCase())}
                                className="text-xs text-neon-green hover:text-green-300"
                              >
                                Restore Record
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Rest of the component remains the same - Create Record Form, Search, Records List */}
        {/* Create Record Form */}
        <motion.div 
          className="glass-card p-8 rounded-xl mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
            <Plus className="h-6 w-6 mr-2 text-neon-green" />
            Create New Academic Record
          </h2>
          
          <form onSubmit={handleCreateRecord} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input
              type="text"
              value={formData.roll_number}
              onChange={(e) => setFormData({...formData, roll_number: e.target.value.toLowerCase()})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Roll Number (Case Insensitive - e.g., 229x1a2856)"
              required
            />
            
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Student Name"
              required
            />
            
            <input
              type="text"
              value={formData.current_year_sem}
              onChange={(e) => setFormData({...formData, current_year_sem: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Current Year & Current Sem (e.g., IV/II)"
              required
            />
            
            <input
              type="text"
              value={formData.passed_out}
              onChange={(e) => setFormData({...formData, passed_out: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Passed Out Year (if applicable)"
            />
            
            <input
              type="email"
              value={formData.gmail}
              onChange={(e) => setFormData({...formData, gmail: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Gmail Address"
              required
            />
            
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Phone Number"
              required
            />
            
            <input
              type="number"
              step="0.01"
              min="0"
              max="10"
              value={formData.cgpa}
              onChange={(e) => setFormData({...formData, cgpa: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="CGPA (0.00 - 10.00)"
              required
            />
            
            <input
              type="number"
              min="0"
              value={formData.backlogs}
              onChange={(e) => setFormData({...formData, backlogs: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Number of Backlogs"
              required
            />
            
            <textarea
              value={formData.remarks}
              onChange={(e) => setFormData({...formData, remarks: e.target.value})}
              className="md:col-span-2 w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none h-20 resize-none"
              placeholder="Remarks (Optional)"
            />
            
            <motion.button
              type="submit"
              disabled={loading}
              className="md:col-span-2 w-full py-4 bg-gradient-to-r from-neon-blue to-purple-600 hover:from-purple-600 hover:to-neon-blue rounded-lg font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <Loader className="animate-spin h-5 w-5 mx-auto" />
              ) : (
                <>
                  <Plus className="h-5 w-5 mr-2 inline" />
                  Create Academic Record
                </>
              )}
            </motion.button>
          </form>
        </motion.div>

        {/* Search and Filter */}
        <motion.div 
          className="glass-card p-6 rounded-xl mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
            <Search className="h-6 w-6 mr-2 text-neon-blue" />
            Search & Filter Records
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              value={searchParams.roll_number}
              onChange={(e) => setSearchParams({...searchParams, roll_number: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Roll Number (Case Insensitive)"
            />
            
            <input
              type="text"
              value={searchParams.name}
              onChange={(e) => setSearchParams({...searchParams, name: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
              placeholder="Student Name"
            />
            
            <select
              value={searchParams.status}
              onChange={(e) => setSearchParams({...searchParams, status: e.target.value})}
              className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="deleted">Deleted</option>
            </select>
            
            <motion.button
              onClick={handleSearch}
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-neon-green to-green-600 hover:from-green-600 hover:to-neon-green rounded-lg font-semibold text-white disabled:opacity-50 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? <Loader className="animate-spin h-4 w-4 mx-auto" /> : 'Search Records'}
            </motion.button>
          </div>
        </motion.div>

        {/* Records List */}
        <motion.div 
          className="glass-card p-6 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
            <List className="h-6 w-6 mr-2 text-neon-green" />
            Academic Records ({records.length})
          </h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader className="animate-spin h-12 w-12 text-neon-blue" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <AlertTriangle className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-xl">No records found</p>
              <p>Create your first academic record above</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {records.map((record, index) => (
                <motion.div
                  key={record.roll_number}
                  initial={{ opacity: 0, y: 50, rotateY: -15 }}
                  animate={{ opacity: 1, y: 0, rotateY: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6, type: "spring" }}
                >
                  <Card3D
                    record={record}
                    onUpdate={handleUpdateRecord}
                    onDelete={handleDeleteRecord}
                    type="institution"
                  />
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default InstitutionDashboard;
